import { decodePermissions } from './appAccess';

/**
 * rendererLock.js — derive a single renderer-lock verdict from the several
 * sources AE Drive learns about it from.
 *
 * Lock is now SOFT: a locked (non-exempt) user still sees the live TX view, but
 * every mutating control is disabled and no broker/graphic command is sent (see
 * useBrokerService.sendMutation and the handler guards in TXLive). This util is
 * the single source that computes that flag, reused in TXLivePage and TXLive.
 *
 * Sources, in preference order:
 *   1. `readOnly`   — a generic "no actions" prop (e.g. SharedSessionPage's
 *                     always-read-only shared view). Forces locked=true, but
 *                     without a lockedByName (a shared view isn't "locked").
 *   2. `lockInfo`   — live lock state from checkRendererLockStatus polling /
 *                     the ?session= payload, the most current signal. This is
 *                     ALREADY exemption-aware (the backend checks the
 *                     renderers.lock.bypass permission — see checkRendererLock
 *                     in BB_SessionService), so it needs no extra check here.
 *   3. `selectedRenderer.owner_lock` — carried on the renderers list, available
 *                     immediately on selection (avoids a flash of "unlocked"
 *                     before the first poll resolves). This is the RAW lock flag
 *                     straight off the renderer and is NOT exemption-aware — so
 *                     we apply the same exemption the backend uses (lock owner or
 *                     renderers.lock.bypass permission) against the current
 *                     `session` here. Without this, an exempt user would be
 *                     wrongly gated by the raw flag even though the backend
 *                     exempts them.
 *
 * Plain function (no React state) to match the codebase's utils/viewMode.js
 * convention.
 */

// Permission that lets a user bypass a renderer's lock (act on it even though
// someone else locked it) — mirrors BB_SessionService's checkRendererLock
// (session.controller.js), which checks the same string via @ae/auth's
// hasPermission(). This is an RBAC permission, not a hardcoded role check: any
// role granted this permission (via @ae/auth's role->permission mapping, once
// the login JWT embeds it) is exempt, not just a fixed "super admin" role set.
// Read from the JWT's permissions[] claim the same way appAccess.js does.
const RENDERER_LOCK_BYPASS_PERMISSION = 'renderers.lock.bypass';

const hasLockBypassPermission = (session) => {
  const token = session?.accessKey || session?.accessToken || null;
  const perms = decodePermissions(token);
  // '*' is @ae/auth's ALL_PERMISSIONS wildcard, held by super_admin.
  return perms.includes('*') || perms.includes(RENDERER_LOCK_BYPASS_PERMISSION);
};

/**
 * Is the current session exempt from a given owner_lock (the person who locked
 * it, or anyone holding renderers.lock.bypass)? Mirrors checkRendererLock's
 * exemption in BB_SessionService.
 */
const isExemptFromOwnerLock = (ownerLock, session) => {
  if (!ownerLock?.locked) return true; // not locked → trivially "exempt"
  if (hasLockBypassPermission(session)) return true;
  const uid = session?.userId || session?.user_id || null;
  return !!(uid && ownerLock.locked_by_user_id && uid === ownerLock.locked_by_user_id);
};

export function deriveRendererLock({ readOnly, lockInfo, selectedRenderer, session } = {}) {
  // owner_lock fallback only counts if it's locked AND the current viewer is not
  // exempt (lock owner / renderers.lock.bypass permission). lockInfo is already
  // exemption-aware.
  const ownerLock = selectedRenderer?.owner_lock;
  const ownerLockApplies =
    ownerLock?.locked === true && !isExemptFromOwnerLock(ownerLock, session);

  const isRendererLocked =
    !!readOnly ||
    lockInfo?.locked === true ||
    ownerLockApplies;

  // Name/time only come from a real lock (not the generic readOnly view). Only
  // surface the owner_lock name when it actually applies to this viewer.
  const lockedByName =
    lockInfo?.lockedByName || (ownerLockApplies ? ownerLock?.locked_by_name : null) || null;
  const lockedAt =
    lockInfo?.lockedAt || (ownerLockApplies ? ownerLock?.locked_at : null) || null;

  return { isRendererLocked, lockedByName, lockedAt };
}
